import React, { useState, useEffect, useRef } from "react";
import DataGrid, {
  Column,
  IDataGridOptions,
  Summary,
  TotalItem,
  RequiredRule,
  StringLengthRule,
  CustomRule
} from "devextreme-react/data-grid";
import { Template } from 'devextreme-react/core/template';
import { Utilities, Enums, moment, Constants, numeral, String } from "@utils";
import { AEAFieldProps } from "@/components/ui/aea-field/aea-field";
import AEAFieldEditor from "../aea-field/aea-field-editor/aea-field-editor";
import styles from "./aea-batch-grid.module.scss";
import { AEAExcelModal } from '@/components';
import Loader from '../aea-fade-loader/Loader';
import { CheckBox } from "devextreme-react";


export interface BatchGridColumnProps extends AEAFieldProps {
  dataField: string;
  width?: number;
  alignment?: string;
  calculateCellValue?: any;
  format?: string;
  visible?: boolean;
  groupIndex?: number;
  sortOrder?: string;
  minValue?: number;
  maxValue?: number;
  maxLength?: number;
  lengthRuleMessage?: string;
  requiredErrorMessage?: string;
  required?: boolean;
  acceptCustomValue?: boolean;
  editorOptions?: any;
  requiredRegExp?: boolean;
  cssClass?: string;
  handleChange?: boolean;
}
export interface AEABatchGridProps {
  columns: BatchGridColumnProps[];
  dataSource: any[];
  gridProps?: IDataGridOptions;
  onRowInserted?(value: any): void;
  onSelectionChanged?(value: any): void;
  summaryRows?: TotalItem[];
  onCellPreRender?(
    data: any,
    column: BatchGridColumnProps,
    checkLe: boolean
  ): BatchGridColumnProps;
  readOnly?: boolean;
  formMasters?: any;
  modalExcel?: boolean;
  gridDataExcel?: any;
  presenter?: any;
  validateNums?: boolean;
  RegionCountry?: any;
  checkContent?: boolean;
  currentData?: boolean;
  setCheckLEMri?: any
  handleChange?: any
  addModifiedAttr?: boolean
}

const AEABatchGrid = (props: AEABatchGridProps) => {
  const [selectionStart, setSelectionStart] = useState(false);
  const [selectedCells, setSelectedCells]: any = useState({});
  const [dataSourceExcel, setDataSourceExcel]: any = useState(null);
  const [contentReady, setContentReady] = useState(null);
  const [checkLE, setCheckLE] = useState(false);
  const _dxGrid = useRef();

  useEffect(() => { if(dataSourceExcel) setDataSourceExcel(null) },
    [props.dataSource]
  );

  let internalProps: IDataGridOptions = {
    showBorders: true,
    showRowLines: true,
    allowColumnResizing: true,
    columnResizingMode: "widget",
    rowAlternationEnabled: false,
    hoverStateEnabled: true,
    paging: { enabled: false },
    editing: {
      mode: "cell",
      allowUpdating: true, 
      allowAdding: true,
      allowDeleting: true,
      useIcons: true,
    },
    columnFixing: { enabled: true },
    columnWidth: 150,
  };

  internalProps = { ...internalProps, ...props.gridProps };
  if (props.readOnly) {
    delete internalProps.editing;
  }
  if (props?.gridProps?.scrolling) {
    internalProps.className = styles.gridContainerFixed;
  }

  if(props.checkContent){
    internalProps.onContentReady = () => { setContentReady(true) }
  }

  //#region test ctrl +c
  const onGridMouseDown = () => {
    setSelectionStart(true);
  };
  const onGridMouseUp = () => {
    setSelectionStart(false);
  };
  const onCellOver = (props: any, e: any) => {
    if (selectionStart) {
      const _selectedCells = { ...selectedCells };
      if (
        _selectedCells[props.rowIndex.toString()] === null ||
        _selectedCells[props.rowIndex.toString()] === undefined
      ) {
        _selectedCells[
          props.rowIndex.toString()
        ] = props.columnIndex.toString();
      }
      setSelectedCells(_selectedCells);
    }
  };

  const handleRowUpdated = (e: any) => {
    if(!!props.addModifiedAttr && props.addModifiedAttr == true) e.data.isModified = true;
  };

  const cellIsSelected = (rowIndex: number, columnIndex: number): boolean => {
    if (Utilities.isNullObject(selectedCells[rowIndex.toString()])) {
      return false;
    }
    return selectedCells[rowIndex.toString()] === columnIndex.toString();
  };

  useEffect(() => {
    const grid: DataGrid = _dxGrid.current;
  }, []);
  //#endregion

  const renderCellTemplate = (container: any,  options: any, column: BatchGridColumnProps) => {
    if (!Utilities.isNullObject(column.onValueChanged)) {
      column.onValueChanged(options, props.columns);
    }
    let _value = options.value;
    const _format = column.format;
    switch (column.fieldType) {
      case Enums.FieldTypes.Select:
          if (options.value === 0) { options.value = undefined; }
          if (!Array.isArray(options.value) && !Utilities.isNullEmptyObject(options.value)) { 
            options.value = [options.value];
          }
          let _selectItems: any[] =  column.allItems ? column.allItems : column.items

          _selectItems = _selectItems.filter((_item) => options.value?.includes(_item.id))
            .map((_item) => _item.label);
          _value = _selectItems.join(",");
          
          if(column.dataField === Enums.ProaBasicFields.IdContrapartida) _value = (options.value?.length && options.value[0]?.substring(0,3) == "NEW") ? options.value[0].slice(3) : _value
        break;
      case Enums.FieldTypes.Date:
        _value = options.value ? moment.utc(options.value).tz(Constants.GeneralFormats.TIME_ZONE).format(
          _format ? _format : Constants.GeneralFormats.DATE_FORMAT
        ) : "";        
        break;
      case Enums.FieldTypes.currency:
        if(!Utilities.isNullEmptyObject(options.value)) options.value = Number(options.value.toFixed(2)) 
        _value =  (!Utilities.isNullEmptyObject(options.value)) ? `${numeral(options.value).format(
          _format ? _format : Constants.GeneralFormats.CURRENCY_FORMAT_NUMERAL
        )} €` : '';  
        if(props.validateNums) _value = (options.value) ? _value : "";    
        break;        
      case Enums.FieldTypes.Number:
        if(!Utilities.isNullEmptyObject(options.value)) options.value = Number(options.value.toFixed(2))  
        _value = _value ? `${numeral(options.value).format(
          _format ? _format : Constants.GeneralFormats.CURRENCY_FORMAT_NUMERAL
        )}` : _value;        
        if(props.validateNums) _value = (options.value) ? _value : "";    
        break;
    }
    container.textContent = _value;
    if (column.readOnly) {
      container.classList.add(styles.AEABatchGridReadOnly);
    }
    if (column.cssClass){
      container.classList.add(styles[column.cssClass]);
    }
    container.classList.add(styles.heigthPl)
  };
  const renderFieldTemplate = (column: BatchGridColumnProps, cellProps: any ) => {
    let _column = column;
    if (!Utilities.isNullObject(props.onCellPreRender)) {
      _column = props.onCellPreRender(cellProps.data, column, checkLE);
    }
    return (
      <AEAFieldEditor
        {..._column}
        onValueChanged={(e) => {cellProps.data.setValue(e);}}
        value={cellProps.value}
        defaultValue={cellProps.value}
      ></AEAFieldEditor>
    );
  };
  const getRequiredRule = (column: BatchGridColumnProps) => {
    return column.required ? (
      <RequiredRule message={column.requiredErrorMessage} />
    ) : (
      String.Empty
    );
  };
  const getStringLengthRule = (column: BatchGridColumnProps) => {
    return column.maxLength ? (
      <StringLengthRule
        message={column.lengthRuleMessage}
        max={column.maxLength}
      />
    ) : (
      String.Empty
    );
  };
  const validatePep = (e: any) => {
    if(e.value){
      let regex = /^\w{4}\.\w{4}\.\w{4}\.\w(.{6})$/gi
      return regex.test(e.value.toString())
    } else return false;
  };
  const getRequiredRulePEP = (column: BatchGridColumnProps) => {
    return column.requiredRegExp ? (
      <CustomRule ignoreEmptyValue={true} validationCallback={validatePep} message={column.requiredErrorMessage}/>
    ) : (
      String.Empty
    );
  };
  const renderColumn = (column: BatchGridColumnProps) => {
    const commonProps = {
      cellTemplate: (container: any, _props: any) => renderCellTemplate(container, _props, column),
      dataField: column.dataField,
      caption: column.title,
      width: column.width,
      allowEditing: !column.readOnly,
      alignment: column.alignment,
      calculateCellValue: column.calculateCellValue,
      visible: column.visible,
      groupIndex: column.groupIndex,
      sortOrder: column.sortOrder,
      acceptCustomValue: column.acceptCustomValue,
      editorOptions: column.editorOptions || null
    };

    switch (column.fieldType) {
      case Enums.FieldTypes.Checkbox:
        //delete commonProps.cellTemplate;
        delete commonProps.cellTemplate;        
        return (
          <Column 
            {...commonProps} 
            key={column.id}
          >
            <AEAFieldEditor {...column} fieldType={Enums.FieldTypes.Checkbox} onValueChanged={column.onValueChanged}></AEAFieldEditor> 
          </Column>
        );        
      case Enums.FieldTypes.Select:        
        return (
          <Column
            {...commonProps}
            key={column.id}
            editCellComponent={(cellProps) =>
              renderFieldTemplate(column, cellProps)
            }
          >
            <AEAFieldEditor {...column}></AEAFieldEditor>
          </Column>
        );
      case Enums.FieldTypes.Button:
        return (
          <Column 
            dataType={"string"}
            key={column.fieldType}
            {...column}
          ></Column>
        )
      default:
        return (
          <Column
            {...commonProps}
            key={column.id}
            dataType={
              column.fieldType === Enums.FieldTypes.currency
                ? Enums.FieldTypes.Number
                : column.fieldType
            }
          >
            {getRequiredRule(column)}
            {getStringLengthRule(column)}
            {column.requiredRegExp ? getRequiredRulePEP(column) : null}
          </Column>
        );
    }
  };

  const saveDataExcel = (dataSource: any) => {
    let newDataSource = dataSourceExcel || props.dataSource || []
    let cont = newDataSource.length + 1;
    let hasAnio = newDataSource ? newDataSource[newDataSource.length - 1]?.PPresuAnio || null : null;
    newDataSource = newDataSource.concat(dataSource)
    if (dataSource){
      dataSource = dataSource.map((e: any) => {
        //Fixing Index column
        e.Indice = cont++;
        //Fixing PPresuAnio column
        if (hasAnio){
          hasAnio++;
          e.PPresuAnio = hasAnio;
        }
        return e;
      })
    }

    setDataSourceExcel(newDataSource)
    props.presenter.openCloseModalExcel()
  };

  const toolbarItemRender = () => {
    return (
      <div style={{display:"flex", gap:"10px"}}>
        <CheckBox
          key="checkLE"
          id="checkLE"
          value={checkLE}
          onValueChanged={(e) => { setCheckLE(e.value); props.setCheckLEMri(e.value) }}
        />
        {props.presenter.currentLabels.get('FilterMRIIndicators')}
      </div>
    )
  }

  const customEditorPreparing = (e: any)=> {
    if (e.parentType === 'dataRow') {
        const defaultValueChangeHandler = e.editorOptions.onValueChanged;
        e.editorOptions.onValueChanged = function (args: any) { 
          if (args.value != "" && args.value < 0 && !!props.presenter &&( e.dataType == Enums.FieldTypes.Number || e.dataType == Enums.FieldTypes.currency) && (args.value != null && args.value != undefined)){
            Utilities.notifyToast(props.presenter.currentLabels.get('AlertNegativeValues'))
            e.setValue(null);
          }else if(!!props.handleChange){
            defaultValueChangeHandler(args)
            e.data = e.row?.data
            props.handleChange?.(e)
          } else defaultValueChangeHandler(args);
        }
    }
  }

  return (
    <div style={{position: 'relative'}}>
      {props.checkContent && <Loader loading={!(contentReady && props.currentData)} currentData={props.currentData} />}
      <DataGrid
        ref={_dxGrid}
        onRowUpdated={handleRowUpdated}
        onRowInserted={props.onRowInserted}
        onSelectionChanged={props.onSelectionChanged}
        onEditorPreparing={customEditorPreparing}
        {...internalProps}
        dataSource={dataSourceExcel || props.dataSource}
      >
        {props.summaryRows ? (
          <Summary>
            {props.summaryRows.map((sumConfig: any, index: number) => {
              return <TotalItem key={index} {...sumConfig} />;
            })}
          </Summary>
        ) : null}
        {props.columns.map(renderColumn)}
        {!!props.setCheckLEMri && <Template name="checkLE" render={toolbarItemRender} />}
      </DataGrid>
      {props.modalExcel ? 
        <AEAExcelModal 
          formMasters={props.formMasters}
          RegionCountry={props.RegionCountry}
          openModal={props.modalExcel}
          gridDataExcel={props.gridDataExcel}
          originalData={props.dataSource}
          onCancel={props.presenter.openCloseModalExcel}
          onSaveDataExcel={saveDataExcel}
          cancelButton={props.presenter.currentLabels.get('Cancelar')}
          titleExcel={props.presenter.currentLabels.get('PegarDesdeExcel')}
          labelExcel1={props.presenter.currentLabels.get('PegarDesdeExcelInfo1')}
          labelExcel2={props.presenter.currentLabels.get('PegarDesdeExcelInfo2')}
          saveButton={props.presenter.currentLabels.get('InsertarFilas')}></AEAExcelModal>  
      : null}
    </div>
  );
};

export default AEABatchGrid;
